import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, TrendingUp, AlertTriangle, Lightbulb, Tags } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function AIFinancialAdvisorCard({ orgId }: { orgId: string }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<any>(null);

  const run = async () => {
    setLoading(true);
    setReport(null);
    try {
      const { data, error } = await supabase.functions.invoke("ai-financial-advisor", {
        body: { orgId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setReport(data.report);
      toast.success("Financial analysis ready");
    } catch (e: any) {
      toast.error(e.message || "Failed to generate analysis");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">AI Financial Advisor</h3>
        </div>
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing</> : "Run analysis"}
        </Button>
      </div>

      {!report && !loading && (
        <p className="text-sm text-muted-foreground">
          Click <b>Run analysis</b> to get CFO-grade insights on your margins, cost leaks, and pricing.
        </p>
      )}

      {report && (
        <div className="space-y-5">
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
            <p className="text-sm font-medium">{report.verdict}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <h4 className="text-sm font-semibold">Key wins</h4>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {report.key_wins?.map((w: string, i: number) => <li key={i}>• {w}</li>)}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <h4 className="text-sm font-semibold">Red flags</h4>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {report.red_flags?.map((w: string, i: number) => <li key={i}>• {w}</li>)}
              </ul>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-primary" />
              <h4 className="text-sm font-semibold">Cost optimizations</h4>
            </div>
            <div className="space-y-2">
              {report.cost_optimizations?.map((c: any, i: number) => (
                <div key={i} className="p-3 rounded-md border bg-card">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{c.area}</p>
                    <Badge variant="outline">{c.est_impact}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{c.suggestion}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Tags className="w-4 h-4 text-primary" />
              <h4 className="text-sm font-semibold">Pricing recommendations</h4>
            </div>
            <div className="space-y-2">
              {report.pricing_recommendations?.map((p: any, i: number) => (
                <div key={i} className="p-3 rounded-md border bg-card">
                  <p className="text-sm font-medium">{p.scenario} → {p.action}</p>
                  <p className="text-xs text-muted-foreground mt-1">{p.rationale}</p>
                </div>
              ))}
            </div>
          </div>

          {report.quickbooks_value && (
            <div className="p-3 rounded-md bg-muted text-xs text-muted-foreground">
              <b>QuickBooks tip:</b> {report.quickbooks_value}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
